import { memo, useMemo, useRef, useState } from 'react';

import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import {
  CollapsibleCard,
  CollapsibleCardBody,
  CollapsibleCardHeader,
} from '@clay-run/glaze/collapsible-card';
import { InputField } from '@clay-run/glaze/input-field';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { TextareaField } from '@clay-run/glaze/textarea-field';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { RichTextBodyField } from '../../_shared/components/rich-text';
import type { CampaignMember } from '../../_shared/launcher-campaign.types';
import { buildTemplateVars } from '../../_shared/launcher-copy-tokens';
import type { CopyPreviewCampaign, CopyTokenGap } from '../copy-token-flags';
import { toSenderValue } from '../copy-token-flags';
import { reviewBadgeState } from '../generate-with-ai-logic';
import type { StepReview } from '../generated-copy-store';
import { gongStepTypeLabel } from '../gong-flow-picker-api';
import { sequenceTokenFlags, tokenFlagBadgeText } from '../sequence-token-flags';
import {
  isGongEmailStep,
  NON_EMAIL_SUBJECT_HINT,
  REPLY_STEP_NO_SUBJECT_TEXT,
} from '../step-content-rules';
import {
  clearCopyDraft,
  clearCopyDraftIfUnchanged,
  copyDraftKey,
  editCopyDraft,
  isCopyDraftDirty,
  resolveCopyDraftValues,
  selectClearEpoch,
  selectCopyDraftEntry,
  selectResetGeneration,
  useSequenceCopyDraftStore,
} from '../sequence-copy-draft-store';
import type { SequenceStep } from '../schemas';
import { resolveTokenTarget, type TokenTargetField } from '../token-insert';
import { useCaretTracker, type PlainTextAccess } from '../use-caret-tracker';
import { isUsageSidebarOpened } from '../usage-sidebar-errors';
import { useAutosizeTextarea } from '../use-autosize-textarea';
import { StepReviewCard } from './StepReviewCard';
import { TokenPicker } from './TokenPicker';

/**
 * U2 (plan `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`,
 * decision 8) — shown under a non-email step's Body field, unconditionally
 * (whether or not the step currently carries text), since it explains what
 * blank vs. typed MEANS for a step the rep is actively editing. Preview
 * sibling: `GONG_BLANK_STEP_TEXT` (`step-content-rules.ts`), shown instead
 * of this sentence when the step is actually blank — `SequencesStep.tsx`'s
 * `SamplePreviewCard` has no live draft to explain, only the saved outcome.
 */
const NON_EMAIL_BODY_HINT =
  'Leave this blank and Gong runs its own script for this step. Anything you write here replaces it for the contacts you send to.';

/**
 * Launcher V2 QA-fixes E1 — `StepEditorShell` extracted verbatim out of
 * `play-flow/SequencesStep.tsx` (plan Group E, E1 step 1).
 *
 * This is the ONE half of that file's per-step editor that has zero rung/
 * play/mapping coupling: its props (`step`, `sequenceId`, `draftScope`,
 * `onSave`) and its draft store (`sequence-copy-draft-store.ts`, keyed by
 * {@link copyDraftKey}) never reference a rung, a Play, or a playbook mapping
 * DIRECTLY — `draftScope` is an opaque string the caller resolves.
 * `SequenceEditorShell` — the rung rail/mapping/Gong-flow/roster-gap wrapper
 * this shell used to sit inside — does NOT extract this cleanly and stays in
 * `SequencesStep.tsx`.
 *
 * Two call sites as of this unit:
 *  - `SequencesStep.tsx`'s `SequenceEditorShell`, passing `draftScope=
 *    {rung.draftKey}` (D1 shared-edit-leak fix) so a draft belongs to THIS
 *    rung, not to every rung mapping the same sequence.
 *  - `SequenceCopyPane.tsx` (E1 step 2), the Config/Library page's direct
 *    sequence-content editor — no rung exists there at all, so it just
 *    passes the literal `draftScope="library"`.
 *
 * 2026-09-02 rip-out (U3) — `isReadOnly` (a rung pinned to a historical
 * "locked at send" version) is removed wholesale: every rung is always
 * editable now, and this editor's existing dirty/Discard/Save state machine
 * is the whole story. No replacement editability gate was added.
 *
 * `onSave` is owned by the caller: it merges this step's edited values into
 * the sequence's full steps array and posts the real whole-snapshot write —
 * `SequencesStep`'s caller may route that write through a branch (D1) or the
 * main line depending on sharing; `SequenceCopyPane`'s caller always writes
 * main line (the Library page has no Play/branch concept). Either way, a
 * sequence version is a self-contained whole-snapshot (Key Decision 12), so
 * there is no per-step endpoint for this shell to call directly.
 *
 * ── F2 (Batch 3, live-QA round 2, decision 10) — `bodyFormat` ─────────────
 * `bodyFormat: 'rich'` swaps the plain `TextareaField` Body for
 * `RichTextBodyField` (Gong bodies are HTML — see the plan's "Existing Gong
 * read infrastructure" section). The one genuinely fiddly part is the
 * DOM-island seedKey: `RichHtmlField`/`RichTextBodyField` only re-touch their
 * contentEditable DOM when `seedKey` changes, so it has to change on exactly
 * the moments an EXTERNAL write should replace what's on screen, and never
 * on the rep's own keystrokes (a keystroke already commits straight to the
 * draft store via `onChange`, which never touches `seedKey`). The four
 * ingredients:
 *  - `draftKey` — a different step/scope is a different DOM island, full stop.
 *  - `renderedVersionId` (the caller's `sequenceQuery.data?.head.versionId` /
 *    `renderedVersion.versionId`) — bumps when a Save lands (the draft is
 *    cleared and the head moves to the new version): re-seeding then shows
 *    content IDENTICAL to what was already on screen, so it's harmless; it
 *    also covers a server head moving under a clean step.
 *  - `resetGeneration` (`selectResetGeneration`, `sequence-copy-draft-store.ts`)
 *    — bumped by {@link clearCopyDraft} (Discard) AND by
 *    `acceptGeneratedCopyDraft` (Accept generated copy — Codex
 *    adversarial-review bug 1, 2026-09-02). Neither action's own effect (the
 *    entry disappearing or being overwritten in the store) changes either of
 *    the other ingredients, so without this counter a Discard OR an Accept on
 *    a dirty/generated rich body would leave stale HTML sitting in the DOM
 *    even though `value` had already moved on.
 *  - `clearEpoch` (`selectClearEpoch`, `sequence-copy-draft-store.ts`) —
 *    bumped by `clearSequenceCopyDrafts` (`PlayFlowShell`'s `[playId]`
 *    reset — Codex adversarial-review bug 2, 2026-09-02). That reset runs in
 *    a passive `useEffect`, i.e. AFTER a new Play's tree (this component
 *    included) has already committed its first render, so a new Play's
 *    `draftKey`/`renderedVersionId`/`resetGeneration` can collide with the
 *    previous Play's. This module-scope, unscoped-by-key counter guarantees
 *    the tuple changes across every clear regardless of that collision.
 * `'plain'` mode ignores all of this — `TextareaField` is a normal controlled
 * input with no seed concept, so its `value` prop alone is the whole story.
 */

export interface SequenceStepEditorProps {
  step: SequenceStep;
  /**
   * QA round 7, U5 (item 7) — THE step number shown in the header
   * ("Step {ordinal}"). Threaded by the host, NEVER derived here: this
   * component sees one step, not the sequence, so it cannot sort or index
   * against its siblings. The host computes it as `stepOrdinal(sortedSteps,
   * step.position)` (`gong-flow-picker-api.ts`) — the step's 1-based index in
   * the POSITION-SORTED list, the same number `toOverrideSteps` sends Gong
   * and `DispatchInspector` already renders. `data-step-review` stays keyed
   * by `step.position`, not this value — it is a stable identity key, not a
   * label.
   */
  ordinal: number;
  sequenceId: string;
  /**
   * The editing SURFACE this step's draft belongs to (D1 shared-edit-leak
   * fix) — a rung's `draftKey` (`playbook-draft.ts`'s `rungDraftKey()` /
   * `new:N`) in the play flow, or the literal `'library'` on the Config
   * page's `SequenceCopyPane`. Threaded straight into
   * `sequence-copy-draft-store.ts`'s `copyDraftKey`, so two callers editing
   * the same `(sequenceId, position)` under different scopes never share an
   * entry — see that store's module doc for the full rationale.
   */
  draftScope: string;
  /**
   * F2 (Batch 3) — the SEQUENCE'S currently-rendered version id (the
   * caller's `sequenceQuery.data?.head.versionId` / `renderedVersion.versionId`,
   * NOT this one step's identity — a version is a whole-snapshot array with
   * no per-step id, Key Decision 12). One of the three `bodyFormat: 'rich'`
   * seedKey ingredients — see this file's module doc. Unused in `'plain'`
   * mode, but always required so a future flip to `'rich'` on an existing
   * call site can't forget it.
   */
  renderedVersionId: string | null;
  /**
   * Whether GONG will treat this step as a REPLY in the previous step's
   * thread — in which case the Subject field is not rendered at all, because
   * Gong REJECTS an override that sets a subject on one and 400s the ENTIRE
   * enrollment over it (not just the offending step).
   *
   * Computed BY THE HOST, never here, and deliberately REQUIRED rather than
   * defaulted: the answer is the step's ORDINAL in the position-sorted
   * sequence checked against the BOUND FLOW's live reply-step orders
   * (`isReplyAtOrdinal` + `use-gong-reply-step-orders.ts`), which is the rule
   * `send-payload.ts#toOverrideSteps` already obeys and therefore the only
   * rule that can be right. This component can't compute it — it sees one
   * step, not the sequence, and not the flow. Making it required is what stops
   * a third host from silently inheriting a `false` default and reintroducing
   * the bug below.
   *
   * This used to be derived HERE, from `gongStepIsReply(step.gongStepDescriptor)`
   * (now deleted). The descriptor's only producer never wrote `is_reply`, so
   * the answer was ALWAYS false: the field rendered on every reply step and
   * the send silently blanked whatever the rep typed into it (Devin D3). And
   * even with the producer fixed, the descriptor is the IMPORTED step's flag —
   * reply-ness belongs to the flow's SLOT, not to the copy sitting in it, so
   * the first reorder made it lie in both directions (Codex C4).
   */
  isReplyStep: boolean;
  onSave: (position: number, values: { subject: string; body: string }) => Promise<SequenceStep>;
  /**
   * F2 (Batch 3, decision 10) — `'rich'` renders `RichTextBodyField` for the
   * Body field (Gong sequences: bodies are HTML); `'plain'` (the default)
   * keeps today's `TextareaField` unchanged. See this file's module doc for
   * the seedKey discipline `'rich'` mode requires.
   */
  bodyFormat?: 'plain' | 'rich';
  /**
   * U7a — the "Generate with AI" review surface. Present ONLY while this
   * step has a staged generation the rep is reviewing (a per-step slice of
   * `generated-copy-store.ts`'s entry); `undefined` is today's ordinary
   * hand-edit mode, unchanged.
   *
   * When present, this component swaps its whole body for
   * `StepReviewCard`'s two-column Current/Generated review and HIDES the
   * hand-edit Discard/Save row below (decisions 2/12 — the two edit
   * surfaces are mutually exclusive per step, and a review in progress owns
   * the authoritative draft, not `sequence-copy-draft-store.ts`). The
   * header's "Unsaved changes" badge is likewise replaced by
   * {@link reviewBadgeState}'s Staged/Accepted/Rejected/No-change badge for
   * the review's own decision.
   */
  review?: {
    entry: StepReview;
    /**
     * Bumped by the caller ONLY when `entry.staged` is replaced — never on
     * a keystroke. See `StepReviewCard`'s own doc for why this is a
     * separate seedKey ingredient from `resetGeneration`/`clearEpoch` below.
     */
    stagedGeneration: number;
    onEdit: (patch: { subject?: string; body?: string }) => void;
    onDecide: (decision: StepReview['decision']) => void;
    /** Token gaps against the GENERATED copy — see `StepReviewCard`'s own doc. Omitted/empty renders no warning row. */
    tokenGaps?: CopyTokenGap[];
  };
  /**
   * D22 (Codex F-K) — hides the hand-edit Discard/Save row for the WHOLE
   * duration of a "Generate with AI" session at this step's editing
   * surface, independently of whether `review` is present yet. `review`
   * present-or-not already hides the row for its OWN reason (a review in
   * progress owns the authoritative draft) — this prop instead covers the
   * two windows `review` can't: before generation has produced ANY frame
   * (session open, `review` still `undefined` for every step), and any
   * step whose own frame simply hasn't arrived yet while sibling steps'
   * have. `SequenceCopyPane.tsx` passes its `isGenerateSessionOpenHere`
   * flag here unconditionally, not gated on a `stepReview` existing.
   * Defaults `false` (today's ordinary hand-edit behavior, unaffected).
   */
  handSaveSuppressed?: boolean;
  /**
   * U9b — the token picker's live-preview context (R10). All three are
   * `null`/`undefined`-tolerant: `SequenceCopyPane` (Library mode) passes
   * literal `null`s (no rung/campaign concept there — the picker rows render
   * "No sample"); `SequencesStep.tsx` passes its real `sampleMember`/
   * `campaign`/`effectiveSender(sequenceSummary, mapping)`; a caller that
   * omits all three (every pre-U9 test/call site) gets the same "No sample"
   * rows as an explicit `null`, since `sampleVars` below only resolves when
   * `sampleMember` is truthy.
   *
   * `campaign` is checked against `undefined` specifically, not falsiness:
   * `null` is `useLauncherCampaign`'s real "still loading" value and a legal
   * `buildTemplateVars` input (campaign-scoped tokens just read blank), so it
   * must still reach that call — only an outright omitted prop should count
   * as "no campaign context wired here at all".
   */
  sampleMember?: CampaignMember | null;
  campaign?: CopyPreviewCampaign | null;
  /** The sequence's effective sender (a `SENDER_TOKENS` value or a static email), or `null` when unset. */
  sender?: string | null;
}

/**
 * Playbooks QA round 4, U11 (E1) / Decision 14 — `memo`'d.
 *
 * Why it helps: `SequencesStep` renders one of these PER STEP, and every
 * keystroke in any one of them re-rendered the whole shell (the draft store
 * subscription) and therefore all of its siblings. Each sibling re-render
 * re-ran that editor's own draft-store reads and rich-body seedKey work for
 * copy that had not changed.
 *
 * What makes the memo actually bite (a memo whose props change every render
 * is pure overhead): `onSave` is now a `useCallback` in `SequencesStep` and
 * `bodyFormat` comes from a memoized per-position map — the two props that
 * used to be rebuilt inline on every render.
 *
 * Where it does NOT bite, stated rather than assumed: in generation-review
 * mode the caller passes a freshly-constructed `review` object (with inline
 * `onEdit`/`onDecide` closures and a recomputed `tokenGaps`), so those
 * renders still go through. That is the correct trade — the review surface is
 * driven by the generation store, not by keystrokes in another step, and
 * memoizing `review` would mean memoizing three closures and a token scan for
 * a surface that only exists while a generation is being reviewed. Ordinary
 * hand-edit mode (`review === undefined`) is the typing path, and that is the
 * one this protects.
 */
export const SequenceStepEditor = memo(function SequenceStepEditor({
  step,
  ordinal,
  sequenceId,
  draftScope,
  renderedVersionId,
  isReplyStep,
  onSave,
  bodyFormat = 'plain',
  review,
  handSaveSuppressed = false,
  sampleMember = null,
  campaign,
  sender = null,
}: SequenceStepEditorProps) {
  const [open, setOpen] = useState(true);
  /**
   * Round-4-adversarial-review fix (Bug 2) — the Save button must stay
   * disabled for the WHOLE duration of `onSave`, not just the network call
   * after a gate choice. `onSave` (the caller's `handleSaveStep`) is the
   * ENTIRE save-gate flow — read `activeMappingCount`, maybe open the
   * Save-vs-Save-as-new-copy confirm, wait for the rep to choose, THEN post —
   * and this button had no pending state at all before this fix, so nothing
   * stopped a rep from clicking Save again while a previous click's gate
   * dialog (or its post-choice write) was still in flight, opening a SECOND
   * gate dialog and potentially creating a second, independent duplicate.
   * See `SequencesStep.tsx`'s/`SequenceCopyPane.tsx`'s own `choiceMade` guard
   * for the belt-and-suspenders half of this fix, needed because a rapid
   * double-click on the SAME dialog's own button can still land before this
   * component even re-renders with `isSaving: true`.
   */
  const [isSaving, setIsSaving] = useState(false);
  /**
   * U9 (item 18, R12) — the draft lives in `sequence-copy-draft-store.ts`, NOT
   * in `useDraftState`. A rail click (or, for `SequenceCopyPane`, collapsing a
   * different Library row) UNMOUNTS this component's parent — and
   * `useDraftState` is component-local `useState`, which survives same-key
   * re-renders (its actual job) but never an unmount. Typed copy was silently
   * destroyed by switching away.
   *
   * `head` is read from the `step` prop on every render, and the store entry
   * (present only while dirty) overrides it — so a clean step follows a server
   * head that moved under it, while a dirty one is never clobbered. See the
   * store's module doc for the seed-once contract.
   */
  const head = { subject: step.subject ?? '', body: step.body ?? '' };
  // Live-QA round 2 (C1) — a cheap nicety: an imported Gong step's header
  // shows its real step type, when the (opaque, Key Decision 12)
  // `gongStepDescriptor` carries one. `null` for every non-Gong-imported
  // step, so this renders nothing for them — additive only.
  const gongStepType = gongStepTypeLabel(step.gongStepDescriptor);
  // U2 (plan `2026-09-24-004-...`, decisions 1/4/8) — a non-email Gong step
  // (Call / LinkedIn Connect / LinkedIn Message / Custom) is editable like
  // any other step. This flag only gates the two additive hint captions
  // below (Subject's "optional" caption, Body's blank/override hint) — it
  // never hides a field, a button, or `review`.
  const isNonEmailStep = !isGongEmailStep(step);
  const draftKey = copyDraftKey(draftScope, sequenceId, step.position);
  const entry = useSequenceCopyDraftStore(selectCopyDraftEntry(draftKey));
  const isDirty = isCopyDraftDirty(entry);
  const value = resolveCopyDraftValues(entry, head);
  const setDraft = (patch: { subject?: string; body?: string }) =>
    editCopyDraft(draftKey, head, patch);
  // U8 — authoring flags (Gong-unmapped tokens, unsupported conditionals,
  // unknown/typo tokens, the Clay-owned `{{Company Name}}` token) derived
  // LIVE from `value` (the same `edited ?? staged` source `tokenGaps` reads
  // elsewhere in this file — see `head`/`entry`/`resolveCopyDraftValues`
  // above), never from the saved `step` prop. Pure derivation over text, no
  // effect, no stored column (plan Key Decision 10) — mirrors the
  // `isDirty`/"Unsaved changes" non-blocking posture: it never touches Save,
  // Continue, or readiness.
  const tokenFlagBadge = useMemo(
    () =>
      tokenFlagBadgeText(
        sequenceTokenFlags([{ position: step.position, subject: value.subject, body: value.body }])
      ),
    [step.position, value.subject, value.body]
  );
  // F2 (Batch 3) — third seedKey ingredient for `bodyFormat: 'rich'`; see this
  // file's module doc. Unused (but still subscribed to, cheaply — a Map read)
  // in `'plain'` mode.
  const resetGeneration = useSequenceCopyDraftStore(selectResetGeneration(draftKey));
  // Codex adversarial-review bug 2 (2026-09-02) — fourth seedKey ingredient.
  // `PlayFlowShell`'s `[playId]` reset runs in a passive `useEffect`, i.e.
  // AFTER a new Play's tree (this component included) has already committed
  // its first render, so a new Play's `[draftKey, renderedVersionId,
  // resetGeneration]` tuple can collide with the previous Play's (a clear
  // resets `resetGenerations` to `{}`, and an unset key already reads as
  // generation 0 either side of that). This module-scope counter bumps on
  // EVERY `clearSequenceCopyDrafts()` call, with no per-key scoping, so the
  // seedKey tuple always changes across a clear regardless of that collision.
  // See `sequence-copy-draft-store.ts`'s `selectClearEpoch` doc.
  const clearEpoch = useSequenceCopyDraftStore(selectClearEpoch);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // Codex hardening fix: `open` is the remount-detection key — see
  // `use-autosize-textarea.ts`'s header. Collapsing/reopening this card
  // unmounts/remounts the `<textarea>` (Base UI's `Collapsible.Panel`
  // defaults to `keepMounted=false`), and without this the hook's effect
  // deps (`[ref, value]`) could both stay unchanged across that remount, so
  // the freshly-mounted node never got re-measured.
  useAutosizeTextarea(bodyRef, value.body, open);
  // U9b (R10) — the hand-edit fields' own caret tracker. A fresh instance per
  // `SequenceStepEditor` mount: field keys ('subject'/'body') only need to be
  // unique WITHIN this component, never globally, since `StepReviewCard`
  // (review mode, a mutually-exclusive branch below) owns its own instance.
  const tracker = useCaretTracker<TokenTargetField>();
  // The wrapper `div` `richProps` resolves the live `[contenteditable="true"]`
  // root through — see `use-caret-tracker.ts`'s `resolveRichRoot` doc for why
  // this can't be a ref straight onto `RichHtmlField` itself (no imperative
  // handle, core file).
  const richBodyRef = useRef<HTMLDivElement>(null);
  // See the `sampleMember`/`campaign`/`sender` prop doc above for why
  // `campaign` is checked against `undefined` specifically.
  const sampleVars =
    sampleMember && campaign !== undefined
      ? buildTemplateVars(sampleMember, campaign, toSenderValue(sender))
      : null;
  // The field this step's ONE Tokens button inserts into — see
  // `resolveTokenTarget`'s doc.
  const tokenTarget = resolveTokenTarget(tracker.lastField, isReplyStep);
  // Both arms read `value` — the resolved draft, never the head.
  const tokenAccess: Record<TokenTargetField, PlainTextAccess> = {
    subject: { getText: () => value.subject, setText: (subject) => setDraft({ subject }) },
    body: { getText: () => value.body, setText: (body) => setDraft({ body }) },
  };

  async function handleSave() {
    // Captured BEFORE the await: whatever else the rep types while the write
    // is in flight must not be dropped by this save (see
    // `clearCopyDraftIfUnchanged`).
    const dispatched = value;
    setIsSaving(true);
    try {
      await onSave(step.position, dispatched);
      // Drop the draft ONLY on success: `onSave` has already written the
      // server's response into the sequence query cache, so this step's `step`
      // prop is the saved copy by the time the entry goes away — the fields
      // never flicker back to the pre-save head. A failure falls through to
      // the catch and leaves the draft (and its "Unsaved changes" marker)
      // exactly where they were, which is the whole point of the marker.
      clearCopyDraftIfUnchanged(draftKey, dispatched);
      addToast({ content: 'Step saved.', status: 'success' });
    } catch (error) {
      // U2a — the sentinel `saveWithGate` throws when it defers to the
      // "Where is this used" sidebar instead of writing directly: no error,
      // so no toast — the draft (and its "Unsaved changes" marker) stays
      // exactly as it was, and `isSaving` still clears via the `finally`
      // below. An ordinary save failure still gets the normal error toast.
      if (isUsageSidebarOpened(error)) return;
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not save this step — try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    // terra-qyt.12 (U8) — `data-step-review` is the jump-to-card anchor
    // `GenerateWithAIDrawer`'s review-stage decision list scrolls to.
    // `CollapsibleCard` accepts only `children/className/defaultOpen/open/
    // onOpenChange` and does NOT spread arbitrary attributes (verified,
    // `@clay-run/glaze/src/collapsible-card.tsx:12-18`) — the wrapping `div`
    // is required, the attribute cannot go on `CollapsibleCard` itself.
    <div data-step-review={step.position}>
      <CollapsibleCard open={open} onOpenChange={setOpen} className="border-border-secondary">
        <CollapsibleCardHeader>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Text size="sm" weight="semibold">
              Step {ordinal}
            </Text>
            {gongStepType && <Badge>{gongStepType}</Badge>}
            {/*
              U8 (plan Key Decision 10) — same non-blocking posture as the
              "Unsaved changes" marker below: a flag, never a gate. Rendered
              whenever the LIVE draft (`value`, not the saved `step`) has an
              authoring issue, independent of review/dirty state.
            */}
            {tokenFlagBadge && <StatusBadge status="warning">{tokenFlagBadge}</StatusBadge>}
            {/*
              D3 (U9, item 18) — a NON-BLOCKING marker, matching the prototype's
              wording (`playbooks-prototype-v6.html:6625`). It never gates Save,
              Continue, or the Ready badge: `rungReadiness` deliberately still
              means "the SAVED steps are ready" (`sequence-readiness.ts`), so a
              rep with unsaved typing sees both "Ready" and this.
            */}
            {review ? (
              // U7a — replaces the hand-edit "Unsaved changes" badge while a
              // review is present: the authoritative draft is the review's
              // own decision, not `sequence-copy-draft-store.ts`'s dirty flag
              // (which stays untouched — and so unrenderable — for the whole
              // duration of a review, since the fields below come from
              // `StepReviewCard`, not this component's own `InputField`/
              // `TextareaField`/`RichTextBodyField`).
              (() => {
                const badge = reviewBadgeState(review.entry);
                return <StatusBadge status={badge.status}>{badge.label}</StatusBadge>;
              })()
            ) : (
              isDirty && <StatusBadge status="warning">Unsaved changes</StatusBadge>
            )}
          </div>
        </CollapsibleCardHeader>
        <CollapsibleCardBody className="border-border-secondary flex flex-col gap-3 p-4">
          {review ? (
            <StepReviewCard
              step={step}
              bodyFormat={bodyFormat}
              review={review.entry}
              stagedGeneration={review.stagedGeneration}
              draftKey={draftKey}
              resetGeneration={resetGeneration}
              clearEpoch={clearEpoch}
              onEdit={review.onEdit}
              onDecide={review.onDecide}
              tokenGaps={review.tokenGaps}
              sampleMember={sampleMember}
              campaign={campaign}
              sender={sender}
              isReplyStep={isReplyStep}
            />
          ) : (
            <>
          {isReplyStep ? (
            // Gong sends a reply step in the previous step's thread and
            // REJECTS an override that sets a subject on one — it 400s the
            // ENTIRE enrollment, not just the offending step. So the field is
            // not rendered at all rather than shown-and-disabled: there is no
            // value a rep could type here that Gong would accept, and a
            // disabled input invites them to wonder what unlocks it.
            //
            // Say WHY, not just that it is unavailable — the rep needs to know
            // the prospect still sees a subject line.
            <Text size="sm" className="text-text-secondary">
              {REPLY_STEP_NO_SUBJECT_TEXT}
            </Text>
          ) : (
            <>
              <InputField
                label="Subject"
                value={value.subject}
                onChange={(e) => setDraft({ subject: e.target.value })}
                {...tracker.plainProps('subject')}
              />
              {/*
                U2 (plan `2026-09-24-004-...`, decision 4) — Gong ACCEPTS a
                subject on a non-email step and renders it as the call
                script's title (`docs/flow/gong-flow-play-spec-handoff.md:123-125`);
                the only documented subject rejection is on a REPLY ordinal,
                which is the `isReplyStep` branch above, not this one.
              */}
              {isNonEmailStep && (
                <Text size="xs" variant="tertiary">
                  {NON_EMAIL_SUBJECT_HINT}
                </Text>
              )}
            </>
          )}
          {bodyFormat === 'rich' ? (
            // F2 (Batch 3) — no `<label>` binds to a contentEditable div (labels
            // only bind to labelable elements — the same lesson the Gong
            // follow-up bundle's `StepOverrideEditor` already learned the hard
            // way: a wrapping `<label>` auto-bound to the toolbar's Bold button
            // instead and hijacked click activation). `aria-label` is this
            // field's real accessible name.
            //
            // U9b (R10) — the wrapper `div` `richProps` resolves the live
            // contentEditable root through (see `use-caret-tracker.ts`'s
            // `resolveRichRoot` doc): `RichHtmlField` exposes no ref/imperative
            // handle of its own, so caret tracking listens on this wrapper
            // instead, where `keyup`/`mouseup`/`blur` from the contentEditable
            // child still bubble up to it.
            <div ref={richBodyRef} {...tracker.richProps('body', richBodyRef)}>
              <RichTextBodyField
                value={value.body}
                // `Key` (React's seedKey type) is `string | number`, no `null` —
                // `renderedVersionId` is `null` before the sequence's first
                // version has loaded, so it's coerced to the sentinel `'none'`
                // here. Harmless: a real versionId can never equal that string
                // (server-minted ids), and the loading state renders no editor
                // at all (`renderedVersion === null` short-circuits above this
                // component's mount entirely), so this branch's seedKey is only
                // ever actually read once a real id is available.
                seedKey={[draftKey, renderedVersionId ?? 'none', resetGeneration, clearEpoch]}
                onChange={(body) => setDraft({ body })}
                className="mt-1 min-h-[4.5rem] w-full rounded border border-border-secondary p-2 text-sm"
                aria-label="Body"
              />
            </div>
          ) : (
            <TextareaField
              ref={bodyRef}
              label="Body"
              value={value.body}
              onValueChange={(body) => setDraft({ body })}
              resizable={false}
              // `className` on `TextareaField`/`Textarea` lands on the OUTER
              // wrapper `<span>`, not the `<textarea>` itself (see
              // `textarea.tsx`'s two separate `clsx` lists) — `style` is the one
              // prop that flows straight through to the raw element (Textarea's
              // `...props` rest spread, merged onto `<textarea>` via
              // `mergeProps`), so a min-height guard has to go here rather than
              // as a Tailwind class. Roughly 3 text rows, so an empty Body never
              // renders as a sliver before/between measurements.
              style={{ minHeight: '4.5rem' }}
              {...tracker.plainProps('body')}
            />
          )}
          {/*
            U2 (plan `2026-09-24-004-...`, decisions 1/2/3/8) — shown for
            every non-email step regardless of whether it currently carries
            text: blank means Gong runs its own script
            (`GONG_BLANK_STEP_TEXT`, the preview's sibling sentence, shown
            there instead of here); typing here replaces it for the contacts
            sent, resolved and defect-checked exactly like an email body.
          */}
          {isNonEmailStep && (
            <Text size="xs" variant="tertiary">
              {NON_EMAIL_BODY_HINT}
            </Text>
          )}
          {/* THE step's one token picker. `tokenTarget` is the single source of
              both the insert target and the panel's "Insert into …" header. */}
          <TokenPicker
            target={tokenTarget}
            sampleVars={sampleVars}
            sender={sender}
            onInsert={(token) => tracker.insert(tokenTarget, token, tokenAccess[tokenTarget])}
          />
          {/*
            D22 (Codex F-K) — the hand-edit Discard/Save row is suppressed for
            the whole duration of a "Generate with AI" session, not only once
            this exact step's own review frame has arrived (`review`, above,
            only hides it once THAT happens). See `handSaveSuppressed`'s own
            doc on `SequenceStepEditorProps`.
          */}
          {!handSaveSuppressed && (
            <div className="flex justify-end gap-2">
              {/*
                Discard drops the draft entry, which reverts the fields to the
                SAVED head (the store holds nothing but the diff — see its module
                doc). It writes nothing: an already-saved version is untouched by
                throwing away this session's typing. Rendered alongside Save and
                disabled when clean, the same treatment Save already had, rather
                than the prototype's appear-on-dirty pair — a control that
                materializes mid-typing shifts the buttons under the cursor.
              */}
              <Button
                variant="inline-text"
                size="sm"
                onClick={() => clearCopyDraft(draftKey)}
                disabled={!isDirty || isSaving}
              >
                Discard
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleSave()}
                disabled={!isDirty || isSaving}
                loading={isSaving}
              >
                Save
              </Button>
            </div>
          )}
            </>
          )}
        </CollapsibleCardBody>
      </CollapsibleCard>
    </div>
  );
});
